import React, { useState, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  Pane,
  TextInput,
  Spinner,
  Menu,
  Position,
  Popover,
  SearchIcon,
  Text,
  Alert
} from 'evergreen-ui';
import { SEARCH_PUBLISHERS } from '../../graphql/queries/Publishers/QueryPublishers';
import debounce from 'lodash.debounce';

/**
 * SuperAdminSearchBar component
 * Allows searching for publishers by name and displays results in a dropdown
 *
 * @param {Object} props
 * @param {Function} props.onSelectPublisher - Callback when a publisher is selected
 */
const SuperAdminSearchBar = ({ onSelectPublisher }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Use Apollo's useLazyQuery to fetch publishers when the search term changes
  const [searchPublishers, { loading, error, data }] = useLazyQuery(SEARCH_PUBLISHERS, {
    fetchPolicy: 'network-only', // Don't use cache
    onCompleted: () => {
      setIsSearching(false);
      setShowResults(true);
    },
    onError: () => {
      setIsSearching(false);
    }
  });

  // Create a debounced search function to avoid too many requests
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((term) => {
      if (term.trim().length > 1) {
        searchPublishers({ variables: { searchTerm: term, limit: 10 } });
        setIsSearching(true);
      } else {
        setShowResults(false);
      }
    }, 300),
    []
  );

  // Update search term and trigger search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Handle publisher selection
  const handleSelectPublisher = (publisher) => {
    setSearchTerm(publisher.name);
    setShowResults(false);
    onSelectPublisher(publisher);
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowResults(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <Pane marginBottom={24} position="relative">
      <Pane display="flex" alignItems="center">
        <TextInput
          width="100%"
          height={40}
          placeholder="Search publishers by name..."
          value={searchTerm}
          onChange={handleSearchChange}
          paddingLeft={40}
        />
        <SearchIcon position="absolute" left={12} color="muted" />
        {isSearching && <Spinner position="absolute" right={12} size={16} />}
      </Pane>

      {error && (
        <Alert intent="danger" marginTop={8}>
          Error searching publishers: {error.message}
        </Alert>
      )}

      {showResults && data && data.publisherSearch && data.publisherSearch.length > 0 && (
        <Popover
          position={Position.BOTTOM_LEFT}
          content={
            <Menu>
              {data.publisherSearch.map((publisher) => (
                <Menu.Item
                  key={publisher.id}
                  onSelect={() => handleSelectPublisher(publisher)}
                >
                  <Text>{publisher.name}</Text>
                </Menu.Item>
              ))}
            </Menu>
          }
          isShown={showResults}
        >
          <Pane />
        </Popover>
      )}

      {showResults && data && data.publisherSearch && data.publisherSearch.length === 0 && (
        <Alert intent="warning" marginTop={8}>
          No publishers found matching "{searchTerm}"
        </Alert>
      )}
    </Pane>
  );
};

export default SuperAdminSearchBar;
